import type { CreativeBriefResult } from "@/lib/campaign-draft";

function toStringArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === "string" && val.length > 0) return [val];
  return [];
}

function extractBriefData(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;

  if (r.creative_brief && typeof r.creative_brief === "object") return r.creative_brief as Record<string, unknown>;
  if (r.output && typeof r.output === "object") {
    const out = r.output as Record<string, unknown>;
    if (out.creative_brief && typeof out.creative_brief === "object") return out.creative_brief as Record<string, unknown>;
    return out;
  }
  if (r.data && typeof r.data === "object") {
    const data = r.data as Record<string, unknown>;
    if (data.creative_brief && typeof data.creative_brief === "object") return data.creative_brief as Record<string, unknown>;
    return data;
  }
  if (r.result && typeof r.result === "object") {
    const result = r.result as Record<string, unknown>;
    if (result.creative_brief && typeof result.creative_brief === "object") return result.creative_brief as Record<string, unknown>;
    return result;
  }
  return r;
}

export function normalizeCreativeBriefResponse(raw: unknown): CreativeBriefResult {
  const data = extractBriefData(raw);

  return {
    objective: String(data.objective ?? data.goal ?? ""),
    hook_direction: String(data.hook_direction ?? data.hook ?? ""),
    headline_options: toStringArray(data.headline_options ?? data.headlines),
    copy_direction: String(data.copy_direction ?? data.copy ?? ""),
    visual_direction: String(data.visual_direction ?? data.visual ?? ""),
    structure_template: toStringArray(data.structure_template ?? data.structure),
    proof_elements: toStringArray(data.proof_elements ?? data.proof),
    cta_direction: String(data.cta_direction ?? data.cta ?? ""),
    designer_notes: String(data.designer_notes ?? data.notes ?? ""),
    ai_generation_prompt: String(data.ai_generation_prompt ?? data.prompt ?? ""),
  };
}

const REQUIRED_FIELDS: (keyof CreativeBriefResult)[] = [
  "objective",
  "hook_direction",
  "headline_options",
  "copy_direction",
  "visual_direction",
  "structure_template",
  "cta_direction",
];

export function validateCreativeBrief(brief: CreativeBriefResult): string[] {
  const errors: string[] = [];
  for (const field of REQUIRED_FIELDS) {
    const val = brief[field];
    if (!val || (Array.isArray(val) && val.length === 0) || val === "") {
      errors.push(`Missing required field: ${field}`);
    }
  }
  return errors;
}
